import { ExternalLaunchType } from '@heidi/integrations/ehr/contracts';
import { externalLaunchContextAtom } from '@heidi/integrations/ehr/data/atoms';
import { isRouterMonkeypatchForEhrLaunchKilled } from '@heidi/integrations/flags';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

const isInternalFrameworkRoute = (pathname: string): boolean =>
  pathname === '/api' ||
  pathname.startsWith('/api/') ||
  pathname === '/_next' ||
  pathname.startsWith('/_next/');

// Avoid matching usernames or routes with dots (e.g., /user/john.doe)
const KNOWN_STATIC_EXTENSIONS =
  /\.(png|jpg|jpeg|gif|svg|ico|css|js|json|pdf|woff|woff2|ttf|eot)$/i;
const isPublicFile = (pathname: string): boolean =>
  KNOWN_STATIC_EXTENSIONS.test(pathname);

const pathnameFromUrl = (url: string): string => url.split(/[?#]/, 1)[0] ?? url;

const getConcreteTemplateAs = (
  router: ReturnType<typeof useRouter>,
  url: { pathname?: string; query?: Record<string, string | string[] | undefined> }
): string | undefined => {
  if (!url.pathname?.includes('[')) return undefined;

  let resolvedPath = url.pathname;
  const templateParamNames = new Set<string>();

  // Resolve optional catch-alls before regular dynamic segments. The optional
  // form has two closing brackets (`[[...id]]`), so matching the inner
  // `[...id]` first would leave a bracket behind and make the `as` unusable.
  resolvedPath = resolvedPath.replace(
    /\[\[(\.\.\.)?([^\]]+)\]\]/g,
    (template, catchAll, key: string) => {
      templateParamNames.add(key);
      const value = url.query?.[key];
      if (value === undefined) return '';
      const values = Array.isArray(value) ? value : [value];
      return catchAll
        ? values.map((item) => encodeURIComponent(item)).join('/')
        : encodeURIComponent(values[0] ?? '');
    }
  );

  resolvedPath = resolvedPath.replace(
    /\[(\.\.\.)?([^\]]+)\]/g,
    (template, catchAll, key: string) => {
      templateParamNames.add(key);
      const value = url.query?.[key];
      if (value === undefined) return template;
      const values = Array.isArray(value) ? value : [value];
      return catchAll
        ? values.map((item) => encodeURIComponent(item)).join('/')
        : encodeURIComponent(values[0] ?? '');
    }
  );

  if (resolvedPath.includes('[')) return undefined;

  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(url.query ?? {})) {
    if (templateParamNames.has(key) || value === undefined) continue;
    for (const item of Array.isArray(value) ? value : [value]) {
      query.append(key, item);
    }
  }

  const hash = router.asPath.match(/#.*$/)?.[0] ?? '';
  const search = query.toString();
  return `${resolvedPath}${search ? `?${search}` : ''}${hash}`;
};

const shouldPrefixExternalLaunchUrl = (url: string): boolean => {
  // Absolute or protocol-relative URLs are never prefixed.
  if (/^[a-z][a-z\d+.-]*:/i.test(url) || url.startsWith('//')) return false;
  // Next.js route templates contain brackets (e.g. `/scribe/session/[...id]`).
  // These are never real URLs — prefixing them would navigate to the literal
  // bracket path.
  if (url.includes('[')) return false;
  const pathname = pathnameFromUrl(url);
  if (
    pathname === '/external' ||
    pathname.includes('/external/') ||
    pathname.startsWith('/integration')
  )
    return false;
  if (isInternalFrameworkRoute(pathname)) return false;
  if (isPublicFile(pathname)) return false;

  // Empty and root targets are not navigable app routes.
  if (pathname === '' || pathname === '/') return false;

  return true;
};

/**
 * Transforms a target URL string/object to include the `/external` prefix if required.
 */
const formatPrefixedUrl = <T extends string | object>(url: T): T => {
  if (typeof url === 'string') {
    const formatted = url.startsWith('/') ? url : `/${url}`;
    return `/external${formatted}` as T;
  }

  if (typeof url === 'object' && url !== null && 'pathname' in url) {
    const targetObj = url as { pathname?: string };
    if (targetObj.pathname) {
      const formatted = targetObj.pathname.startsWith('/')
        ? targetObj.pathname
        : `/${targetObj.pathname}`;
      return {
        ...url,
        pathname: `/external${formatted}`,
      };
    }
  }

  return url;
};

/**
 * Keeps an external EHR launch tab on `/external/...` routes after the launch handoff.
 * Safely monkeypatches the local `router` instance methods for seamless navigation.
 */
export const useExternalLaunchRoutePrefix = (): void => {
  const router = useRouter();
  const hasGenericSmartOnFhirLaunchContext =
    useAtomValue(externalLaunchContextAtom)?.launchType ===
    ExternalLaunchType.GENERIC_SMART_ON_FHIR;
  const isRouterMonkeypatchKilled = isRouterMonkeypatchForEhrLaunchKilled();

  useEffect(() => {
    if (!hasGenericSmartOnFhirLaunchContext || !router || isRouterMonkeypatchKilled)
      return;

    // Preserve original router methods
    const originalPush = router.push;
    const originalReplace = router.replace;

    const prefixIfNeeded = <T extends string | object>(url: T): T => {
      const targetUrl =
        typeof url === 'string'
          ? url
          : typeof url === 'object' &&
              url !== null &&
              'pathname' in url &&
              typeof url.pathname === 'string'
            ? url.pathname
            : '';
      return shouldPrefixExternalLaunchUrl(targetUrl) ? formatPrefixedUrl(url) : url;
    };

    const getNavigationArgs = <T extends string | object>(url: T, as?: T) => {
      const targetUrl =
        typeof url === 'string'
          ? url
          : typeof url === 'object' &&
              url !== null &&
              'pathname' in url &&
              typeof url.pathname === 'string'
            ? url.pathname
            : '';
      const shouldPrefixUrl = shouldPrefixExternalLaunchUrl(targetUrl);
      const nextUrl = shouldPrefixUrl ? formatPrefixedUrl(url) : url;
      const templateAs =
        targetUrl.includes('[') && typeof url === 'object' && url !== null
          ? getConcreteTemplateAs(
              router,
              url as {
                pathname?: string;
                query?: Record<string, string | string[] | undefined>;
              }
            )
          : undefined;
      const nextAs = templateAs
        ? prefixIfNeeded(templateAs)
        : as && shouldPrefixUrl
          ? prefixIfNeeded(as)
          : as;

      return { nextUrl, nextAs };
    };

    router.push = (url, as, options) => {
      const { nextUrl, nextAs } = getNavigationArgs(url, as);
      return originalPush.call(router, nextUrl, nextAs, options);
    };

    router.replace = (url, as, options) => {
      const { nextUrl, nextAs } = getNavigationArgs(url, as);
      return originalReplace.call(router, nextUrl, nextAs, options);
    };

    // Immediate check: If user landed directly or refreshed on an un-prefixed URL
    const currentPathname = router.asPath;
    if (shouldPrefixExternalLaunchUrl(currentPathname)) {
      void router.replace(formatPrefixedUrl(currentPathname));
    }

    // Restore original router methods on cleanup
    return () => {
      router.push = originalPush;
      router.replace = originalReplace;
    };
  }, [hasGenericSmartOnFhirLaunchContext, isRouterMonkeypatchKilled, router]);
};
